<template>
  <div
    class="modal-overlay"
    role="dialog"
    aria-modal="true"
    aria-label="Winter Places"
    @click.self="$emit('close')"
  >
    <div class="modal">
      <header class="modal-header">
        <h2 class="modal-title">{{ $t('modals.newbookingmodal.winter_place.title') }}</h2>
        <button class="icon-btn" @click="$emit('close')" aria-label="Close">✕</button>
      </header>

      <div class="canvas-wrap" ref="canvasWrap" :style="bgNaturalW && bgNaturalH ? { aspectRatio: `${bgNaturalW} / ${bgNaturalH}` } : {}">
        <canvas id="marinaPictureCanvas" ref="editorCanvas"></canvas>
      </div>

      <footer class="modal-footer">
        <button class="ui primary button" @click="resetView" aria-label="Reset view">{{ $t('modals.newbookingmodal.winter_place.reset_view') }}</button>
        <button class="ui red button" @click="$emit('close')" aria-label="Close">{{ $t('modals.newbookingmodal.winter_place.close') }}</button>
      </footer>
    </div>
  </div>
</template>

<script>
import { fabric } from 'fabric'
import axios from "axios";

export default {
  name: 'WinterPlaceModal',

  props: {
    selectedMapElement: {
      required: false,
      type: Object
    }
  },

  data() {
    return {
      canvas: null,
      baseW: 800,
      baseH: 600,
      bgNaturalW: null,
      bgNaturalH: null,
      ro: null,
      onEsc: null,
      imageFetched: false,
      pictureId: null,
      imageUrl: '',
      mapElements: [],

      isPanning: false,
      _fit: { s: 1, dx: 0, dy: 0 },
      minZoom: 0.2,
      maxZoom: 8,

      activePointers: new Map(),
      pinching: false,
      pinchStartDist: 0,
      pinchStartZoom: 1,
      pinchStartMid: null,
      lastMidForPan: null,
      lastTapTime: 0,
      lastPos: null,
      isPanningMobile: false,
    }
  },

  mounted() {
    document.documentElement.style.overflow = 'hidden';

    this.canvas = new fabric.Canvas(this.$refs.editorCanvas, {
      preserveObjectStacking: true,
      selection: true,
      enableRetinaScaling: true
    })

    this.fetchImage()

    this.ro = new ResizeObserver(() => this.resizeToContainer());
    this.ro.observe(this.$refs.canvasWrap);

    window.addEventListener('resize', this.resizeToContainer, { passive: true });

    this.onEsc = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.$emit('close');
      }
    };
    window.addEventListener('keydown', this.onEsc, {passive: false});

    this.$nextTick(this.resizeToContainer);

    this.attachViewportHandlers();
    this.attachPointerHandlers();

    this.$refs.editorCanvas.addEventListener('contextmenu', (e) => e.preventDefault());
  },

  beforeDestroy() {
    if (this._boundKeyDownPan) {
      window.removeEventListener('keydown', this._boundKeyDownPan);
      this._boundKeyDownPan = null;
    }
    if (this._boundKeyUpPan) {
      window.removeEventListener('keyup', this._boundKeyUpPan);
      this._boundKeyUpPan = null;
    }

    window.removeEventListener('resize', this.resizeToContainer);
    window.removeEventListener('keydown', this.onEsc);
    if (this.ro) this.ro.disconnect();

    if (this._pointerEl) {
      this._pointerEl.removeEventListener('pointerdown', this._onPointerDown);
      this._pointerEl.removeEventListener('pointermove', this._onPointerMove);
      this._pointerEl.removeEventListener('pointerup', this._onPointerUp);
      this._pointerEl.removeEventListener('pointercancel', this._onPointerUp);
      this._pointerEl.removeEventListener('pointerout', this._onPointerUp);
      this._pointerEl = null;
    }

    if (this.canvas) {
      this.canvas.dispose();
      this.canvas = null;
    }

    document.documentElement.style.overflow = '';
  },

  methods: {
    attachPointerHandlers() {
      if (!this.canvas) return;
      const el = this.canvas.upperCanvasEl ||
        (this.canvas.getSelectionElement ? this.canvas.getSelectionElement() : null);
      if (!el) return;

      this._pointerEl = el;
      this._pointerEl.style.touchAction = 'none';

      this._pointerEl.addEventListener('pointerdown', this._onPointerDown, { passive: false });
      this._pointerEl.addEventListener('pointermove', this._onPointerMove, { passive: false });
      this._pointerEl.addEventListener('pointerup', this._onPointerUp, { passive: true });
      this._pointerEl.addEventListener('pointercancel', this._onPointerUp, { passive: true });
      this._pointerEl.addEventListener('pointerout', this._onPointerUp, { passive: true });
    },

    resizeToContainer() {
      const wrap = this.$refs.canvasWrap;
      if (!wrap || !this.canvas || !this.baseW || !this.baseH) return;

      const rect = wrap.getBoundingClientRect();
      const cw = rect.width;
      const ch = rect.height;

      const s = Math.min(cw / this.baseW, ch / this.baseH) || 1;
      const drawW = this.baseW * s;
      const drawH = this.baseH * s;
      const dx = (cw - drawW) / 2;
      const dy = (ch - drawH) / 2;

      this.canvas.setDimensions({ width: cw, height: ch }, { cssOnly: true });

      const vt = this.canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
      const prevFit = this._fit || { s: 1, dx: 0, dy: 0 };

      const userZoom = vt[0] / prevFit.s;
      const relTx = vt[4] - prevFit.dx;
      const relTy = vt[5] - prevFit.dy;

      const scaleRatio = s / prevFit.s;
      const a = s * userZoom;
      const e = dx + relTx * scaleRatio;
      const f = dy + relTy * scaleRatio;

      this.canvas.setViewportTransform([a, 0, 0, a, e, f]);
      this._fit = { s, dx, dy };

      this.canvas.requestRenderAll();
    },

    async fetchImage() {
      try {
        const response = await this.axios.get(`/merchants/${this.activeMerchant.id}/marina/picture`)

        if (response.data.success) {
          this.imageFetched = true;
          this.pictureId = response.data.pictureId;
          console.log(this.pictureId)
          this.imageUrl = response.data.imagePath;

          this.loadBackgroundImage();
          this.fetchMapElements();
        }
      } catch (error) {
        console.error('Error fetching map elements:', error);
      }
    },

    loadBackgroundImage() {
      if (!this.imageUrl) return;

      fabric.Image.fromURL(this.imageUrl, (img) => {
        if (!img) return;

        this.bgNaturalW = img.width;
        this.bgNaturalH = img.height;

        this.baseW = this.bgNaturalW;
        this.baseH = this.bgNaturalH;

        this.canvas.setDimensions({ width: this.baseW, height: this.baseH });

        img.set({
          originX: 'left',
          originY: 'top',
          left: 0,
          top: 0,
          selectable: false,
          evented: false,
        });

        this.canvas.setBackgroundImage(img, () => {
          this.canvas.requestRenderAll();
          this.resizeToContainer();

          if (this.mapElements && this.mapElements.length) {
            this.renderMapElements();
          }
        });
      }, { crossOrigin: 'anonymous' });
    },

    async fetchMapElements() {
      try {
        const response = await this.axios.get(`/merchants/${this.activeMerchant.id}/marina/pictures/${this.pictureId}/boat-places`);
        if (response.data.exists) {
          this.mapElements = response.data.boatPlaces;
          this.renderMapElements();
        }
      } catch (error) {
        console.error('Error fetching map elements:', error);
      }
    },

    renderMapElements() {
      this.canvas.getObjects().forEach(obj => {
        if (obj.isMapElement) {
          this.canvas.remove(obj);
        }
      });

      this.mapElements.forEach(elementData => {
        if (elementData.shape_type === 'polygon') {
          this.createPolygonOnCanvas(elementData);
        }
      });

      if (this.selectedMapElement && this.selectedMapElement.hasOwnProperty('winterPlace')) {
        let activePolygon = this.findPolygonByName(this.selectedMapElement.winterPlace)

        if (activePolygon) {
          this.canvas.discardActiveObject();
          this.canvas.setActiveObject(activePolygon);
        }
      }

      this.canvas.requestRenderAll();
    },

    findPolygonByName(name) {
      if (!name) {
        return null;
      }

      return this.canvas.getObjects('polygon').find(obj => obj.mapElementData.name === name)
    },

    createPolygonOnCanvas(polygonData) {
      const points = this.toLogicalPoints(polygonData);

      const polygon = new fabric.Polygon(points, {
        fill: 'rgba(128, 128, 128, 0.5)',
        stroke: 'gray',
        strokeWidth: polygonData.strokeWidth || 2,
        strokeUniform: true,
        selectable: true,
        evented: true,
        hasControls: true,
        perPixelTargetFind: true,
        hasBorders: true,
        padding: 0,
        cornerSize: 10,
        cornerColor: 'blue',
        lockMovementX: true,
        lockMovementY: true,
        // Custom properties
        isMapElement: true,
        mapElementId: polygonData.id,
        mapElementData: polygonData,
      });

      polygon.on('mousedown', (options) => {
        if (confirm(this.$t('modals.newbookingmodal.winter_place.confirm_message', {place: polygon.mapElementData.name}))) {
          const payload = {
            id: polygon.mapElementId,
            name: polygon.mapElementData.name,
            raw: polygon.mapElementData
          };
          this.$emit('polygon-selected', payload);
        }

        return false;
      });

      this.canvas.add(polygon);
    },

    toLogicalPoints(el) {
      const coords = el.coordinates || [];

      if (el.units === 'normalized') {
        return coords.map(c => ({ x: c.x * this.baseW, y: c.y * this.baseH }));
      }

      if (el.units === 'imagePixels') {
        return coords.map(c => ({ x: c.x, y: c.y }));
      }

      return coords.map(c => ({ x: c.x, y: c.y }));
    },

    attachViewportHandlers() {
      this.canvas.on('mouse:wheel', (opt) => {
        const e = opt.e;
        let zoom = this.canvas.getZoom();
        const delta = e.deltaY;
        const zoomFactor = Math.pow(0.999, delta);
        zoom *= zoomFactor;
        zoom = Math.max(this.minZoom * this._fit.s, Math.min(zoom, this.maxZoom * this._fit.s));

        const point = new fabric.Point(e.offsetX, e.offsetY);
        this.canvas.zoomToPoint(point, zoom);

        e.preventDefault();
        e.stopPropagation();
      });

      this._boundKeyDownPan = (e) => this._onKeyDownPanToggle(e);
      this._boundKeyUpPan   = (e) => this._onKeyUpPanToggle(e);

      window.addEventListener('keydown', this._onKeyDownPanToggle, { passive: false });
      window.addEventListener('keyup', this._onKeyUpPanToggle, { passive: false });

      this.canvas.on('mouse:down', (opt) => {
        const e = opt.e;
        const isRightButton = e.button === 2;
        if (this.isPanning || isRightButton) {
          this.canvas.setCursor('grabbing');
          this.canvas.defaultCursor = 'grabbing';
          this._isDragging = true;
          this._lastPos = new fabric.Point(e.clientX, e.clientY);

          this.canvas.discardActiveObject();
          this.canvas.requestRenderAll();
          this.canvas.skipTargetFind = true;
        }
      });

      this.canvas.on('mouse:move', (opt) => {
        if (!this._isDragging) return;
        const e = opt.e;
        const p = new fabric.Point(e.clientX, e.clientY);
        const delta = p.subtract(this._lastPos);
        this._lastPos = p;

        this.canvas.relativePan(delta);
      });

      this.canvas.on('mouse:up', () => {
        this._isDragging = false;
        this.canvas.setCursor(this.isPanning ? 'grab' : 'default');
        this.canvas.defaultCursor = this.isPanning ? 'grab' : 'default';
        this.canvas.skipTargetFind = this.isPanning;
      });

      this.canvas.on('mouse:dblclick', () => {
        this.resetView();
      });
    },

    _onKeyDownPanToggle(e) {
      if (!this.canvas) return;
      if (e.code === 'Space' && !this.isPanning) {
        e.preventDefault();
        this.isPanning = true;
        this.canvas.setCursor('grab');
        this.canvas.defaultCursor = 'grab';
        this.canvas.skipTargetFind = true;
      }
    },

    _onKeyUpPanToggle(e) {
      if (!this.canvas) return;
      if (e.code === 'Space' && this.isPanning) {
        e.preventDefault();
        this.isPanning = false;
        this.canvas.setCursor('default');
        this.canvas.defaultCursor = 'default';
        this.canvas.skipTargetFind = false;
      }
    },

    resetView() {
      const { s, dx, dy } = this._fit || { s: 1, dx: 0, dy: 0 };
      this.canvas.setViewportTransform([s, 0, 0, s, dx, dy]);
      this.canvas.requestRenderAll();
    },

    _canvasPointFromEvent(e) {
      const rect = this.canvas.upperCanvasEl.getBoundingClientRect();
      return new fabric.Point(e.clientX - rect.left, e.clientY - rect.top);
    },

    _onPointerDown(e) {
      if (e.target.setPointerCapture) {
        e.target.setPointerCapture(e.pointerId);
      }
      this.activePointers.set(e.pointerId, e);

      const now = Date.now();
      if (now - this.lastTapTime < 250 && this.activePointers.size === 1) {
        this.resetView();
        this.lastTapTime = 0;
        e.preventDefault();
        return;
      }
      this.lastTapTime = now;

      if (this.activePointers.size === 2) {
        const [p1, p2] = Array.from(this.activePointers.values());
        const a = this._canvasPointFromEvent(p1);
        const b = this._canvasPointFromEvent(p2);

        this.pinching = true;
        this.pinchStartDist = a.distanceFrom(b);
        this.pinchStartZoom = this.canvas.getZoom();
        this.pinchStartMid = new fabric.Point((a.x + b.x) / 2, (a.y + b.y) / 2);
        this.lastMidForPan = this.pinchStartMid;
        this.canvas.skipTargetFind = true;
      }
    },

    _onPointerMove(e) {
      if (!this.activePointers.has(e.pointerId)) return;

      this.activePointers.set(e.pointerId, e);

      if (this.pinching && this.activePointers.size >= 2) {
        const [p1, p2] = Array.from(this.activePointers.values()).slice(0, 2);
        const a = this._canvasPointFromEvent(p1);
        const b = this._canvasPointFromEvent(p2);

        const dist = a.distanceFrom(b);
        if (this.pinchStartDist > 0) {
          let zoom = (dist / this.pinchStartDist) * this.pinchStartZoom;

          let fitS = 1
          if (this.fit) {
            fitS = this.fit.s
          }

          const minZ = this.minZoom * fitS;
          const maxZ = this.maxZoom * fitS;
          zoom = Math.max(minZ, Math.min(zoom, maxZ));

          const mid = new fabric.Point((a.x + b.x) / 2, (a.y + b.y) / 2);
          this.canvas.zoomToPoint(mid, zoom);

          const delta = mid.subtract(this.lastMidForPan);
          this.lastMidForPan = mid;
          this.canvas.relativePan(delta);

          this.canvas.requestRenderAll();
          e.preventDefault();
        }
        return;
      }
    },

    _onPointerUp(e) {
      this.activePointers.delete(e.pointerId);

      if (this.activePointers.size < 2 && this.pinching) {
        this.pinching = false;
        this.pinchStartDist = 0;
        this.canvas.skipTargetFind = false;
      }
    },
  },

  computed: {
    activeMerchant () {
      return this.$store.getters.activeMerchant
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  padding: 1rem;
}

.modal {
  background: #fff;
  color: #111;
  width: min(96vw, 1000px);
  max-height: 90vh;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header,
.modal-footer {
  padding: .75rem 1rem;
  display: flex;
  align-items: center;
}
.modal-header {
  justify-content: space-between;
  border-bottom: 1px solid #eee;
}
.modal-footer {
  justify-content: flex-end;
  border-top: 1px solid #eee;
  gap: .5rem;
}
.modal-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.canvas-wrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  touch-action: none;
}
.canvas-wrap > canvas {
  width: 100%;
  height: 100%;
  display: block;
  background: #f8f9fa;
}

/* Buttons */
.btn, .icon-btn {
  cursor: pointer;
  border: none;
  background: #efefef;
  padding: .5rem .75rem;
  border-radius: 8px;
  font-weight: 600;
}
.btn:hover, .icon-btn:hover { background: #e5e5e5; }
.icon-btn { font-size: 1rem; line-height: 1; }

@media (min-height: 700px) {
  .canvas-wrap { min-height: 200px; }
}

@media (max-width: 480px) {
  .modal-title { font-size: 1rem; }
  .btn, .icon-btn { padding: .45rem .6rem; }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/booking/WinterPlaceModal.vue